/**
 * CommandPalette Component
 * LAYER-SHELL: Power-user command palette
 *
 * Power-user command palette (⌘K / Ctrl+K) for quick navigation and actions.
 * Admin-only feature for internal staff.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  UsersIcon,
  TagIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  QueueListIcon,
  ShieldCheckIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import './CommandPalette.scss';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Callback when user logs out.
   */
  onLogout?: () => void;
  /**
   * Additional custom commands to add.
   */
  customCommands?: CommandItem[];
}

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  keywords?: string[];
  category: 'navigation' | 'action' | 'search';
}

// Icon mapping for dynamic icon resolution
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  DocumentTextIcon,
  UsersIcon,
  TagIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  QueueListIcon,
  ShieldCheckIcon,
};

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onLogout,
  customCommands = [],
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  // Define available commands
  const commands: CommandItem[] = useMemo(() => {
    const defaultCommands: CommandItem[] = [
      // Navigation commands
      {
        id: 'nav-transactions',
        label: 'Go to Transactions',
        description: 'View all transactions',
        icon: DocumentTextIcon,
        action: () => { navigate('/admin/transactions'); onClose(); },
        keywords: ['transactions', 'orders', 'list'],
        category: 'navigation',
      },
      {
        id: 'nav-users',
        label: 'Go to Users',
        description: 'Manage user accounts',
        icon: UsersIcon,
        action: () => { navigate('/admin/users'); onClose(); },
        keywords: ['users', 'accounts', 'customers'],
        category: 'navigation',
      },
      {
        id: 'nav-discounts',
        label: 'Go to Discount Codes',
        description: 'Manage discount codes',
        icon: TagIcon,
        action: () => { navigate('/admin/discount-codes'); onClose(); },
        keywords: ['discounts', 'codes', 'coupons', 'promo'],
        category: 'navigation',
      },
      {
        id: 'nav-surveys',
        label: 'Go to Surveys',
        description: 'View survey responses',
        icon: ClipboardDocumentListIcon,
        action: () => { navigate('/admin/surveys'); onClose(); },
        keywords: ['surveys', 'feedback', 'responses'],
        category: 'navigation',
      },
      {
        id: 'nav-reports',
        label: 'Go to Reports',
        description: 'View analytics and reports',
        icon: ChartBarIcon,
        action: () => { navigate('/admin/reports'); onClose(); },
        keywords: ['reports', 'analytics', 'stats', 'metrics'],
        category: 'navigation',
      },
      {
        id: 'nav-case-manager',
        label: 'Go to Case Manager',
        description: 'Case manager dashboard',
        icon: QueueListIcon,
        action: () => { navigate('/case-manager'); onClose(); },
        keywords: ['case', 'manager', 'cm', 'dashboard'],
        category: 'navigation',
      },
      {
        id: 'nav-compliance',
        label: 'Go to Compliance',
        description: 'Compliance review dashboard',
        icon: ShieldCheckIcon,
        action: () => { navigate('/compliance'); onClose(); },
        keywords: ['compliance', 'review', 'approval'],
        category: 'navigation',
      },
      // Action commands
      {
        id: 'action-create-discount',
        label: 'Create Discount Code',
        description: 'Add a new discount code',
        icon: TagIcon,
        action: () => { navigate('/admin/create-discount'); onClose(); },
        keywords: ['create', 'new', 'discount', 'add'],
        category: 'action',
      },
      // Theme toggle command
      {
        id: 'action-toggle-dark-mode',
        label: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
        description: isDark ? 'Use light theme' : 'Use dark theme',
        icon: isDark ? SunIcon : MoonIcon,
        action: () => { toggleTheme(); onClose(); },
        keywords: ['dark', 'light', 'theme', 'mode', 'toggle', 'appearance'],
        category: 'action',
      },
    ];

    // Add logout if handler provided
    if (onLogout) {
      defaultCommands.push({
        id: 'action-logout',
        label: 'Log Out',
        description: 'Sign out of your account',
        icon: ArrowRightOnRectangleIcon,
        action: () => { onClose(); onLogout(); },
        keywords: ['logout', 'signout', 'exit'],
        category: 'action',
      });
    }

    return [...defaultCommands, ...customCommands];
  }, [navigate, onClose, onLogout, customCommands, isDark, toggleTheme]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return commands;

    const lowerQuery = query.toLowerCase();
    const trimmedQuery = query.trim();

    // Check if query looks like a transaction ID (alphanumeric, possibly with dashes)
    const looksLikeTransactionId = /^[A-Za-z0-9-]{4,}$/.test(trimmedQuery);

    const matchedCommands = commands.filter(cmd => {
      const matchLabel = cmd.label.toLowerCase().includes(lowerQuery);
      const matchDescription = cmd.description?.toLowerCase().includes(lowerQuery);
      const matchKeywords = cmd.keywords?.some(k => k.includes(lowerQuery));
      return matchLabel || matchDescription || matchKeywords;
    });

    // Add dynamic search command if query looks like a transaction ID
    if (looksLikeTransactionId) {
      const searchCommand: CommandItem = {
        id: 'search-transaction',
        label: `Go to Transaction "${trimmedQuery}"`,
        description: 'Navigate directly to this transaction',
        icon: MagnifyingGlassIcon,
        action: () => { navigate(`/admin/transaction/${trimmedQuery}`); onClose(); },
        keywords: [],
        category: 'search',
      };
      return [searchCommand, ...matchedCommands];
    }

    return matchedCommands;
  }, [commands, query, navigate, onClose]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands.length]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Small delay to ensure modal is rendered
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredCommands.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, filteredCommands, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector('[data-selected="true"]');
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="command-palette-overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Palette */}
      <div
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Search input */}
        <div className="command-palette__search">
          <MagnifyingGlassIcon className="command-palette__search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="command-palette__input"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search commands"
            aria-activedescendant={filteredCommands[selectedIndex]?.id}
          />
          <kbd className="command-palette__shortcut">ESC</kbd>
        </div>

        {/* Results list */}
        <div className="command-palette__results" ref={listRef} role="listbox">
          {filteredCommands.length === 0 ? (
            <div className="command-palette__empty">
              No commands found for "{query}"
            </div>
          ) : (
            filteredCommands.map((cmd, index) => {
              const Icon = cmd.icon;
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={cmd.id}
                  id={cmd.id}
                  className={`command-palette__item ${isSelected ? 'command-palette__item--selected' : ''}`}
                  onClick={cmd.action}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="option"
                  aria-selected={isSelected}
                  data-selected={isSelected}
                >
                  <Icon className="command-palette__item-icon" />
                  <div className="command-palette__item-content">
                    <span className="command-palette__item-label">{cmd.label}</span>
                    {cmd.description && (
                      <span className="command-palette__item-description">
                        {cmd.description}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <kbd className="command-palette__item-hint">↵</kbd>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div className="command-palette__footer">
          <span className="command-palette__hint">
            <kbd>↑</kbd><kbd>↓</kbd> navigate
          </span>
          <span className="command-palette__hint">
            <kbd>↵</kbd> select
          </span>
          <span className="command-palette__hint">
            <kbd>esc</kbd> close
          </span>
          <span className="command-palette__hint command-palette__hint--separator">|</span>
          <span className="command-palette__hint">
            <kbd>/</kbd> search
          </span>
          <span className="command-palette__hint">
            <kbd>g</kbd><kbd>t</kbd> transactions
          </span>
          <span className="command-palette__hint">
            <kbd>g</kbd><kbd>u</kbd> users
          </span>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
